import React, { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
// import { ReactComponent as GoogleSheetsIcon } from "../../../assets/icons/google-sheets.svg";
import { NewConnectionCard } from "../../../_components/styled/partners/stylesPartners";
import Checkbox from "../../../_components/checkbox/Checkbox";
import { FeatureCodes } from "../../../_components/details/constants";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Divider,
  Icon,
  SimpleGrid,
  Tooltip,
  ModalCloseButton,
  Box,
} from "@chakra-ui/react";
import {
  FaFacebook,
  FaGoogle,
  FaGoogleDrive,
  FaInstagram,
  FaLinkedin,
  FaPinterest,
  FaShopify,
  FaTiktok,
  FaTwitter,
  FaYoutube,
} from "react-icons/fa";

// Connected apps
import ShopifyConnectApp from "./ShopifyConnectApp";
import GoogleDriveConnectApp from "./GoogleDriveConnectApp";
import GoogleSheetsConnectApp from "./GoogleSheetsConnectApp";
import InstagramBusinessConnectApp from "./InstagramBusinessConnectApp";
import InstagramConnectApp from "./InstagramConnectApp";
import FacebookConnectApp from "./FacebookConnectApp";
import LinkedInConnectApp from "./LinkedInConnectApp";
import TwitterConnectApp from "./TwitterConnectApp";
import GoogleMyBusinessConnectApp from "./GoogleMyBusinessConnectApp";
import YouTubeConnectApp from "./YouTubeConnectApp";
import PinterestConnectApp from "./PinterestConnectApp";
import TikTokConnectApp from "./TikTokConnectApp";
import { isFeatureAvailable } from "apps/simplified-web/src/_utils/common";
import TldrUpgradeSubscriptionModal from "apps/simplified-web/src/_components/common/modals/TldrUpgradeSubscriptionModal";

const NewConnectedAppModal = ({
  show,
  handleModalClose,
  onAddNewPartner,
  showOnlySocial,
}) => {
  const { t } = useTranslation(["planner", "common", "accounts"]);
  const shopifyConnectorRef = useRef(null);
  const googleSheetsRef = useRef(null);
  const googleDriveRef = useRef(null);
  const instagramRef = useRef(null);
  const instagramBusinessRef = useRef(null);
  const facebookRef = useRef(null);
  const linkedInRef = useRef(null);
  const twitterRef = useRef(null);
  const pinterestRef = useRef(null);
  const googleMyBusinessAccountRef = useRef(null);
  const youtubeRef = useRef(null);
  const tiktokRef = useRef(null);
  const [followTwitter, setFollowTwitter] = useState(true);
  const [upgradeModal, setUpgradeModal] = useState();
  const subscription = useSelector((state) => state.subscription);
  const canEditAccounts = useSelector(
    (state) => state.permissions.workspacePermissionChecks.canEditAccounts
  );
  const canAddAccountYouTube = useMemo(
    () =>
      isFeatureAvailable(
        subscription,
        FeatureCodes.PLANNER_ADD_ACCOUNT_YOUTUBE,
        false
      ),
    [subscription]
  );

  const openSelectedModal = (modalRef) => () => {
    if (modalRef && modalRef.current) {
      if (modalRef.current.setOpen) {
        modalRef.current.setOpen(true);
        handleModalClose();
      } else {
        console.error("Please add method or imperative handle `setOpen` for ");
      }
    } else {
      console.error("No modal ref passed for selected option");
    }
  };

  const appConnections = [
    {
      logo: FaShopify,
      title: "Shopify",
      description: "Import product to build marketing assets.",
      onClick: openSelectedModal(shopifyConnectorRef),
      display: !showOnlySocial,
    },
    // {
    //   logo: GoogleSheetsIcon,
    //   title: "Google Sheets",
    //   description: "Connect Sheets to manage projects and creatives’ content.",
    //   onClick: openSelectedModal(googleSheetsRef),
    //   display: !showOnlySocial,
    //   isComingSoon: false,
    // },
    {
      logo: FaGoogleDrive,
      title: "Google Drive",
      description:
        "Connect Google Drive to load files and assets for projects.",
      onClick: openSelectedModal(googleDriveRef),
      display: !showOnlySocial,
      isComingSoon: false,
    },
    {
      logo: FaInstagram,
      title: t("connect.instagram_business.title"),
      description: t("connect.instagram_business.description"),
      onClick: openSelectedModal(instagramBusinessRef),
      display: showOnlySocial,
      isComingSoon: false,
    },
    {
      logo: FaInstagram,
      title: t("connect.instagram.title"),
      description: t("connect.instagram.description"),
      onClick: openSelectedModal(instagramRef),
      display: showOnlySocial,
      isComingSoon: false,
    },
    {
      logo: FaFacebook,
      title: t("connect.facebook.title"),
      description: t("connect.facebook.description"),
      onClick: openSelectedModal(facebookRef),
      display: showOnlySocial,
      isComingSoon: false,
    },
    {
      logo: FaLinkedin,
      title: t("connect.linkedin.title"),
      description: t("connect.linkedin.description"),
      onClick: openSelectedModal(linkedInRef),
      display: showOnlySocial,
      isComingSoon: false,
    },
    {
      logo: FaTwitter,
      title: t("connect.twitter.title"),
      description: t("connect.twitter.description"),
      onClick: openSelectedModal(twitterRef),
      display: showOnlySocial,
      isComingSoon: false,
      children: (
        <Box onClick={(event) => { event.stopPropagation(); }} marginTop="1">
          <Checkbox
            isChecked={followTwitter}
            onChange={() => setFollowTwitter(!followTwitter)}
            label={t("connect.twitter.follow")}
            sx={{
              "> span": {
                fontSize: "xs",
              },
            }}
          />
        </Box>
      )
    },
    {
      logo: FaGoogle,
      title: t("connect.google.title"),
      description: t("connect.google.description"),
      onClick: openSelectedModal(googleMyBusinessAccountRef),
      display: showOnlySocial,
      isComingSoon: false,
    },
    {
      logo: FaPinterest,
      title: t("connect.pinterest.title"),
      description: t("connect.pinterest.description"),
      onClick: openSelectedModal(pinterestRef),
      display: showOnlySocial,
      isComingSoon: false,
    },
    {
      logo: FaYoutube,
      title: t("connect.youtube.title"),
      description: t("connect.youtube.description"),
      onClick: canAddAccountYouTube
        ? openSelectedModal(youtubeRef)
        : () => setUpgradeModal({ title: t("upgrade_modal.features.add_youtube_account.title", { ns: "accounts" }) }),
      display: showOnlySocial,
      isComingSoon: false,
    },
    {
      logo: FaTiktok,
      title: t("connect.tiktok.title"),
      description: t("connect.tiktok.description"),
      onClick: openSelectedModal(tiktokRef),
      display: showOnlySocial,
      isComingSoon: false,
    },
  ];

  return (
    <>
      <Modal
        autoFocus={false}
        size="3xl"
        isOpen={show}
        onClose={handleModalClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New connection</ModalHeader>
          <ModalCloseButton />
          <Divider my="1.5" />
          <ModalBody pt="3" pb="6">
            <SimpleGrid columns={2} spacing="10px">
              {appConnections.map((connection, idx) => {
                const {
                  display,
                  logo,
                  onClick,
                  isComingSoon,
                  title,
                  description,
                  children,
                } = connection;

                if (!canEditAccounts && display && !isComingSoon) {
                  return (
                    <Tooltip
                      key={idx}
                      label={t("member_feature_lock", { ns: "common" })}
                      placement="bottom"
                      hasArrow
                      textAlign="center"
                      shouldWrapChildren
                    >
                      <NewConnectionCard isComingSoon={isComingSoon}>
                        <Icon
                          as={logo}
                          h="35px"
                          w="35px"
                          mr="3"
                          color="white"
                        />
                        <div className="info-wrapper">
                          <div className="title">
                            {title}
                            {isComingSoon && (
                              <span className="view-all">
                                {"(Coming soon)"}
                              </span>
                            )}
                          </div>
                          <div className="description">{description}</div>
                          {children}
                        </div>
                      </NewConnectionCard>
                    </Tooltip>
                  );
                }

                return (
                  <React.Fragment key={idx}>
                    {display && (
                      <NewConnectionCard
                        onClick={onClick}
                        isComingSoon={isComingSoon}
                      >
                        <Icon
                          as={logo}
                          h="35px"
                          w="35px"
                          mr="3"
                          color="white"
                        />
                        <div className="info-wrapper">
                          <div className="title">
                            {title}
                            {isComingSoon && (
                              <span className="view-all">
                                {"(Coming soon)"}
                              </span>
                            )}
                          </div>
                          <div className="description">{description}</div>
                          {children}
                        </div>
                      </NewConnectionCard>
                    )}
                  </React.Fragment>
                );
              })}
            </SimpleGrid>
          </ModalBody>
        </ModalContent>
      </Modal>

      <ShopifyConnectApp ref={shopifyConnectorRef} onSuccess={onAddNewPartner} />
      <GoogleSheetsConnectApp ref={googleSheetsRef} onSuccess={onAddNewPartner} />
      <GoogleDriveConnectApp ref={googleDriveRef} onSuccess={onAddNewPartner} />

      <InstagramBusinessConnectApp ref={instagramBusinessRef} onSuccess={onAddNewPartner} />
      <InstagramConnectApp ref={instagramRef} onSuccess={onAddNewPartner} />
      <FacebookConnectApp ref={facebookRef} onSuccess={onAddNewPartner} />
      <LinkedInConnectApp ref={linkedInRef} onSuccess={onAddNewPartner} />
      <PinterestConnectApp ref={pinterestRef} onSuccess={onAddNewPartner} />
      <TwitterConnectApp ref={twitterRef} onSuccess={onAddNewPartner} follow={followTwitter} />
      <GoogleMyBusinessConnectApp ref={googleMyBusinessAccountRef} onSuccess={onAddNewPartner} />
      <YouTubeConnectApp ref={youtubeRef} onSuccess={onAddNewPartner} />
      <TikTokConnectApp ref={tiktokRef} onSuccess={onAddNewPartner} />

      {upgradeModal && (
        <TldrUpgradeSubscriptionModal
          show={true}
          onHide={() => setUpgradeModal(null)}
          title={upgradeModal.title}
        />
      )}
    </>
  );
};

NewConnectedAppModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  onAddNewPartner: PropTypes.func.isRequired,
};

export default NewConnectedAppModal;
